/**
 * AppsflyerIntegrationMmpBridgePage — Mobile MMP Bridge configuration page.
 * Provides S2S key management, event mapping, webhook URL copy, and a recent
 * event feed. Copy deliberately uses neutral "Mobile MMP Bridge" / "MMP
 * Integration" / "Attribution Vendor" phrasing; the brand name appears only
 * once in the page title.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useAppsflyerIntegrationMmpBridgeList } from "./hooks";

const PAGE = "mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10";
const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE =
	"inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800";

interface EventMapRow {
	vendor_event: string;
	internal_event: string;
}

interface RecentEventRow {
	id: string;
	event_id: string;
	event_name: string | null;
	event_type: string | null;
	media_source: string | null;
	received_at: number;
}

function fmtTime(sec: number): string {
	try {
		return new Date(sec * 1000).toISOString().replace("T", " ").slice(0, 19) + "Z";
	} catch {
		return String(sec);
	}
}

export function AppsflyerIntegrationMmpBridgePage() {
	const feed = useAppsflyerIntegrationMmpBridgeList();

	// Webhook URL — displayed for the operator to paste into the attribution
	// vendor's dashboard. Origin comes from the browser at render time.
	const webhookUrl = useMemo(() => {
		if (typeof window === "undefined") return "/api/v1/appsflyer-integration-mmp-bridge";
		return `${window.location.origin}/api/v1/appsflyer-integration-mmp-bridge`;
	}, []);

	// --- S2S key form (local state; persistence is out-of-band via the API) ---
	const [s2sKey, setS2sKey] = useState("");
	const [s2sSaved, setS2sSaved] = useState(false);
	const [s2sError, setS2sError] = useState<string | null>(null);

	const handleS2sSubmit = (e: FormEvent) => {
		e.preventDefault();
		setS2sSaved(false);
		setS2sError(null);
		const trimmed = s2sKey.trim();
		if (trimmed.length < 16) {
			setS2sError("Key must be at least 16 characters.");
			return;
		}
		// The key is persisted via the secret store (tenant-scoped env var)
		// out-of-band. Here we only record that the operator submitted it.
		setS2sSaved(true);
	};

	// --- Event map (local list of vendor -> internal event name pairs) --------
	const [mapRows, setMapRows] = useState<EventMapRow[]>([
		{ vendor_event: "af_install", internal_event: "install" },
		{ vendor_event: "af_purchase", internal_event: "purchase" },
	]);
	const [vendorEvent, setVendorEvent] = useState("");
	const [internalEvent, setInternalEvent] = useState("");
	const [mapError, setMapError] = useState<string | null>(null);

	const handleAddMap = (e: FormEvent) => {
		e.preventDefault();
		setMapError(null);
		const v = vendorEvent.trim();
		const i = internalEvent.trim();
		if (!v || !i) {
			setMapError("Both fields are required.");
			return;
		}
		if (mapRows.some((r) => r.vendor_event === v)) {
			setMapError("Vendor event already mapped.");
			return;
		}
		setMapRows((prev) => [...prev, { vendor_event: v, internal_event: i }]);
		setVendorEvent("");
		setInternalEvent("");
	};

	const handleRemoveMap = (vendor: string) => {
		setMapRows((prev) => prev.filter((r) => r.vendor_event !== vendor));
	};

	// --- Webhook URL copy -----------------------------------------------------
	const [copied, setCopied] = useState(false);
	const handleCopy = async () => {
		setCopied(false);
		try {
			if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(webhookUrl);
			}
			setCopied(true);
		} catch {
			setCopied(false);
		}
	};

	// --- Recent event feed ----------------------------------------------------
	const rawEvents = (feed.data?.items ?? []) as unknown as RecentEventRow[];
	const hasEvents = Array.isArray(rawEvents) && rawEvents.length > 0;

	return (
		<main
			data-testid="appsflyer-integration-mmp-bridge-page"
			className={PAGE}
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-3xl font-semibold text-green-700">
					AppsFlyer MMP Bridge
				</h1>
				<p className="text-sm text-zinc-600">
					Configure the Mobile MMP Bridge: paste your attribution vendor S2S key,
					map vendor events to internal events, and monitor recent postbacks.
				</p>
			</header>

			{/* --- S2S key form -------------------------------------------------- */}
			<section aria-label="S2S key" className={`${CARD} flex flex-col gap-4`}>
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold text-zinc-900">S2S Key</h2>
					<span className={BADGE}>MMP Integration</span>
				</div>
				<form
					onSubmit={handleS2sSubmit}
					className="flex flex-col gap-4"
					data-testid="mmp-s2s-form"
					aria-label="s2s key form"
				>
					<div className="flex flex-col gap-1.5">
						<label htmlFor="mmp-s2s-key" className={LABEL}>
							Attribution Vendor S2S Key
						</label>
						<input
							id="mmp-s2s-key"
							type="password"
							className={INPUT}
							value={s2sKey}
							onChange={(e) => setS2sKey(e.currentTarget.value)}
							placeholder="Paste your server-to-server key"
							data-testid="mmp-s2s-key-input"
							autoComplete="off"
						/>
						<p className="text-xs text-zinc-500">
							The key is used to verify HMAC signatures on inbound postbacks.
						</p>
					</div>
					<div className="flex items-center gap-3">
						<button
							type="submit"
							className={BTN}
							data-testid="mmp-s2s-save"
						>
							Save Key
						</button>
						{s2sSaved ? (
							<p
								className="text-sm text-green-700"
								role="status"
								data-testid="mmp-s2s-saved"
							>
								Key saved.
							</p>
						) : null}
						{s2sError ? (
							<p className="text-sm text-red-600" role="alert" data-testid="mmp-s2s-error">
								{s2sError}
							</p>
						) : null}
					</div>
				</form>
			</section>

			{/* --- Event mapping ------------------------------------------------- */}
			<section aria-label="event map" className={`${CARD} flex flex-col gap-4`}>
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold text-zinc-900">Event Mapping</h2>
					<span className="text-xs text-zinc-500">
						{mapRows.length} mapped
					</span>
				</div>
				<form
					onSubmit={handleAddMap}
					className="flex flex-col gap-3 sm:flex-row sm:items-end"
					data-testid="mmp-map-form"
					aria-label="event mapping form"
				>
					<div className="flex flex-col gap-1.5 sm:flex-1">
						<label htmlFor="mmp-map-vendor" className={LABEL}>
							Vendor Event
						</label>
						<input
							id="mmp-map-vendor"
							type="text"
							className={INPUT}
							value={vendorEvent}
							onChange={(e) => setVendorEvent(e.currentTarget.value)}
							placeholder="af_purchase"
							data-testid="mmp-map-vendor-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5 sm:flex-1">
						<label htmlFor="mmp-map-internal" className={LABEL}>
							Internal Event
						</label>
						<input
							id="mmp-map-internal"
							type="text"
							className={INPUT}
							value={internalEvent}
							onChange={(e) => setInternalEvent(e.currentTarget.value)}
							placeholder="purchase"
							data-testid="mmp-map-internal-input"
						/>
					</div>
					<button
						type="submit"
						className={BTN}
						data-testid="mmp-map-add"
					>
						Add
					</button>
				</form>
				{mapError ? (
					<p className="text-sm text-red-600" role="alert" data-testid="mmp-map-error">
						{mapError}
					</p>
				) : null}

				<div className="overflow-x-auto">
					<table
						className="min-w-full divide-y divide-zinc-200 text-sm"
						data-testid="mmp-map-table"
					>
						<thead>
							<tr className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
								<th className="px-3 py-2">Vendor Event</th>
								<th className="px-3 py-2">Internal Event</th>
								<th className="px-3 py-2 text-right">Action</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{mapRows.length === 0 ? (
								<tr data-testid="mmp-map-empty">
									<td
										colSpan={3}
										className="px-3 py-6 text-center text-sm text-zinc-500"
									>
										No mappings yet. Add your first vendor event above.
									</td>
								</tr>
							) : (
								mapRows.map((r) => (
									<tr
										key={r.vendor_event}
										data-testid={`mmp-map-row-${r.vendor_event}`}
									>
										<td className="px-3 py-2 font-mono text-xs text-zinc-800">
											{r.vendor_event}
										</td>
										<td className="px-3 py-2 text-zinc-800">
											{r.internal_event}
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												onClick={() => handleRemoveMap(r.vendor_event)}
												className={BTN_SECONDARY}
												data-testid={`mmp-map-remove-${r.vendor_event}`}
												aria-label={`Remove ${r.vendor_event}`}
											>
												Remove
											</button>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>

			{/* --- Webhook URL --------------------------------------------------- */}
			<section aria-label="webhook url" className={`${CARD} flex flex-col gap-4`}>
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold text-zinc-900">Webhook URL</h2>
					<span className="text-xs text-zinc-500">HMAC-SHA256 verified</span>
				</div>
				<p className="text-sm text-zinc-600">
					Paste this URL in your attribution vendor dashboard as the S2S
					postback destination.
				</p>
				<div className="flex items-center gap-2">
					<input
						type="text"
						readOnly
						className={`${INPUT} font-mono text-xs`}
						value={webhookUrl}
						data-testid="mmp-webhook-url"
						onFocus={(e) => e.currentTarget.select()}
					/>
					<button
						type="button"
						onClick={handleCopy}
						className={BTN}
						data-testid="mmp-webhook-copy"
					>
						{copied ? "Copied" : "Copy"}
					</button>
				</div>
			</section>

			{/* --- Recent event feed --------------------------------------------- */}
			<section aria-label="recent events" className={`${CARD} flex flex-col gap-4`}>
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold text-zinc-900">Recent Events</h2>
					<span className="text-xs text-zinc-500">
						{hasEvents ? `${rawEvents.length} recent` : "—"}
					</span>
				</div>
				{feed.isLoading ? (
					<p
						className="text-sm text-zinc-500"
						role="status"
						data-testid="appsflyer-integration-mmp-bridge-loading"
					>
						Loading recent events.
					</p>
				) : feed.isError ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="mmp-feed-error"
					>
						{feed.error?.message ?? "Failed to load recent events."}
					</p>
				) : !hasEvents ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="appsflyer-integration-mmp-bridge-empty"
					>
						No recent events. Postbacks will appear here once your attribution
						vendor delivers them.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200 text-sm"
							data-testid="mmp-feed-table"
						>
							<thead>
								<tr className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-600">
									<th className="px-3 py-2">Event ID</th>
									<th className="px-3 py-2">Type</th>
									<th className="px-3 py-2">Name</th>
									<th className="px-3 py-2">Source</th>
									<th className="px-3 py-2">Received</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{rawEvents.map((row) => (
									<tr key={row.id} data-testid={`mmp-feed-row-${row.id}`}>
										<td className="px-3 py-2 font-mono text-xs text-zinc-800">
											{row.event_id}
										</td>
										<td className="px-3 py-2 text-zinc-800">
											{row.event_type ?? "—"}
										</td>
										<td className="px-3 py-2 text-zinc-800">
											{row.event_name ?? "—"}
										</td>
										<td className="px-3 py-2 text-zinc-800">
											{row.media_source ?? "—"}
										</td>
										<td className="px-3 py-2 text-xs text-zinc-500">
											{typeof row.received_at === "number"
												? fmtTime(row.received_at)
												: "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AppsflyerIntegrationMmpBridgePage;
